import { ShapeHandler } from "./shape-handler.interface";
import { Annotation } from "../models/annotation.model";

/**
 * Handler for circle-specific behavior
 * Implements the ShapeHandler interface for circles
 */
export class CircleHandler implements ShapeHandler {
  /**
   * Gets the left position (percentage) of a circle annotation
   */
  getLeft(annotation: Annotation): number {
    return (annotation as any).cx - (annotation as any).r;
  }

  /**
   * Gets the top position (percentage) of a circle annotation
   */
  getTop(annotation: Annotation): number {
    return (annotation as any).cy - (annotation as any).r;
  }

  /**
   * Gets the width (percentage) of a circle annotation (diameter)
   */
  getWidth(annotation: Annotation): number {
    return (annotation as any).r * 2;
  }

  /**
   * Gets the height (percentage) of a circle annotation
   * Height is 0 for circles as we use padding-bottom for aspect ratio
   */
  getHeight(annotation: Annotation): number {
    return 0; // Height will be controlled by padding-bottom
  }

  /**
   * Gets the padding-bottom value for circles (diameter)
   * This creates a perfect circle using CSS aspect ratio trick
   */
  getPaddingBottom(annotation: Annotation): number {
    return (annotation as any).r * 2;
  }

  /**
   * Updates a circle during drag operations based on the drag mode
   */
  updateOnDrag(
    dragMode: string,
    annotation: any,
    dragStart: {
      cx?: number;
      cy?: number;
      r?: number;
    },
    deltaXPercent: number,
    deltaYPercent: number,
    minRadiusPercent: number
  ): any {
    const result = { ...annotation };

    // Apply the appropriate transformation based on drag mode
    switch (dragMode) {
      case "whole":
        // Move the entire circle, ensuring it stays within bounds
        result.cx = Math.max(dragStart.r || 0, Math.min(100 - (dragStart.r || 0), (dragStart.cx || 0) + deltaXPercent));
        result.cy = Math.max(dragStart.r || 0, Math.min(100 - (dragStart.r || 0), (dragStart.cy || 0) + deltaYPercent));
        break;

      case "resize":
      case "center":
        // Resize the circle from the top handle
        // For circles, we only allow symmetric resizing to maintain a perfect circle
        // Calculate the new radius based on the vertical movement (Y)
        const newRadius = Math.max(minRadiusPercent, (dragStart.r || 0) - deltaYPercent);
        result.r = newRadius;
        break;

      default:
        console.warn(`Unknown drag mode for circle: ${dragMode}`);
        break;
    }

    // Enforce minimum radius constraint
    result.r = Math.max(minRadiusPercent, result.r);

    // Ensure circle stays within bounds (0-100%)
    result.cx = Math.max(result.r, Math.min(100 - result.r, result.cx));
    result.cy = Math.max(result.r, Math.min(100 - result.r, result.cy));

    return result;
  }

  /**
   * Converts a circle annotation to points array for storage
   */
  toPoints(annotation: any): { x: number; y: number }[] {
    return [
      { x: annotation.cx, y: annotation.cy }, // Center point
      { x: annotation.cx + annotation.r, y: annotation.cy } // Radius point
    ];
  }
}
